import React, { Fragment, useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import { doReadOrganisationAccessPoints } from '../../../../../redux/thunks/organisation';
import { selectOrganisationAccessPoints } from '../../../../../redux/selectors/organisation';
import { selectCurrentPartner } from '../../../../../redux/selectors/authentication';

import useBreakpoint from '../../../../../hooks/useBreakpoint';

import Table from '../../../../../components/Table/Table';
import { convertToHex, deviceTypeOptions, deviceTypeRender, readerStatusRenderer, statusMapping } from '../../../../../helpers/renderers';
import { statusSearcher } from '../../../../../helpers/searchers';
import { Search } from '../../../../../utils/Search';
import { MoreVert } from '@material-ui/icons';
import { Menu, MenuItem } from '@material-ui/core';
import { useTableMenuStyles } from '../../../../../utils/TableMenuStyles';
import UpdateFirmware from './UpdateFirmware';
import Button from '../../../../../components/Buttons/Button';
import OutlineButton from '../../../../../components/OutlineButton/OutlineButton';
import './styles.scss';
import FirmwareUpdateDetails from './FirmwareUpdateDetails';
import useIsMobile from '../../../../../hooks/useIsMobile';
import MobileTable from '../../../../../components/MobileTable/MobileTable';
import SButton from '../../../../../components/SButton/SButton';
import FlexRow from '../../../../../components/FlexBox/FlexRow';
import Typography from '../../../../../components/Typography/Typography';

const ACUTab = () => {

  const classes = useTableMenuStyles();
  const isMobile = useIsMobile();

  const dispatch = useDispatch();
  const [readers, setReaders] = useState([]);
  const { customerId } = useParams();
  const { accessPoints, currentUser } = useSelector((state) => ({
    accessPoints: selectOrganisationAccessPoints(state),
    currentUser: selectCurrentPartner(state),
  }));

  const [pagination, setPagination] = useState({ total: 0, currentPage: 1, perPage: 7 });
  const [filters, setFilters] = useState({
    serialNumber: '',
    accessPoint: '',
    formattedFirmwareVersion: '',
    macId: '',
    status: '',
    deviceType: '',
    siteName: ''
  });
  const [menu, setMenu] = useState({ show: false, anchorEl: null, sItem: {} });
  const [firmwareUpdate, setFirmwareUpdate] = useState({ show: false, bulkUpdate: false });
  const [selectedReaders, setSelectedReaders] = useState([]);
  const [firmwareDetails, setFirmwareDetails] = useState(false);

  const sites = useMemo(() => accessPoints?.map(ap => ap?.siteId) || [], [accessPoints]);

  const columns = useMemo(() => {
    const commonColumns = [
      {
        title: 'Serial Number',
        field: 'serialNumber',
        name: 'serialNumber',
      },
      {
        title: 'Access Point',
        field: 'accessPoint',
        name: 'accessPoint',
      },
      {
        title: 'Device Type',
        field: 'deviceType',
        name: 'deviceType',
        lookup: deviceTypeOptions,
        // render: (rowData) =>
        //   voca.chain(rowData.deviceType).kebabCase().replaceAll('-', ' ').titleCase().value(),
        render: (columnProps, rowData) => {
          return deviceTypeRender(rowData.deviceType);
        },
        renderM: (columnProps, rowData) => {
          return deviceTypeRender(rowData.deviceType);
        },
      },
      {
        title: 'Site',
        field: 'siteName',
        name: 'siteName',
      },
      {
        title: 'FW Version',
        field: 'formattedFirmwareVersion',
        name: 'formattedFirmwareVersion',
        search: true,
      },
      {
        title: 'Status',
        field: 'status',
        name: 'status',
        render: (_, column) => readerStatusRenderer(column.status, column?.statusColor),
        renderM: (_, column) => readerStatusRenderer(column.status, column?.statusColor),
        customFilterAndSearch: (term, row) => statusSearcher(term, row.status),
      }
    ]

    const actions = [
      {
        title: 'Action',
        action: {
          icon: <MoreVert />,
          props: {
            onClick: (e) => { setMenu({ show: true, anchorEl: e.currentTarget, sItem: e.currentTarget.dataset }) },
          },
        },
        renderM: (columnProps, rowData, row) => {
          return (<MoreVert onClick={(e) => {
            setMenu({ show: true, anchorEl: e.currentTarget, sItem: { row: row, col: 6 } })
          }} />)
        },
      }
    ]

    return {
      allColumns: [...commonColumns, ...actions],
      commonColumns: commonColumns,
      actions: actions
    }
  }, [])

  const mobileFiltersConfig = useMemo(() => {
    return [
      { name: "Serial Number", key: "serialNumber", type: "search" },
      { name: "Access Point", key: "accessPoint", type: "search" },
      { name: "Device Type", key: "deviceType", type: "select", options: deviceTypeOptions },
      { name: "Site Name", key: "siteName", type: "search" },
      { name: "Status", key: "status", type: "search" },
    ]
  }, [])

  useEffect(() => {
    const list = [];
    if (accessPoints?.length > 0) {
      accessPoints?.map((currentAccessPoint) => {
        if (currentAccessPoint?.devices) {
          currentAccessPoint.devices.map((device) => {
            const reader = {
              id: device?.serialNumber || '',
              serialNumber: device?.serialNumber || '',
              accessPoint: currentAccessPoint?.name || '',
              deviceType: device?.type || '',
              macId: device?.macId || '',
              firmwareVersion: device?.firmwareVersion || 0,
              formattedFirmwareVersion: convertToHex(device?.firmwareVersion || 0) || '',
              status: device?.connectivityStatus,
              statusColor: device?.resourceState || '',
              siteId: currentAccessPoint?.siteId,
              siteName: currentAccessPoint?.siteName,
              updateAllowed: device?.updateAllowed,
              rollbackAllowed: device?.rollbackAllowed,
              pdSwId: device?.pdSwId,
              formattedStatus: statusMapping(device?.connectivityStatus)
            };
            list.push(reader);
            return null;
          });
        }

        return null;
      });
    }

    setReaders(list);
    setPagination(prev => ({
      ...prev,
      total: list?.length ? list?.length : 0
    }))
  }, [accessPoints, setPagination]);

  const point = useBreakpoint();

  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    if (point === 'sm' || point === 'xs' || point === 'md')
      if (currentUser)
        dispatch(
          doReadOrganisationAccessPoints({
            partnerId: currentUser.partnerId,
            organisationId: customerId,
            displayNotification: false,
          }),
        );
  }, [dispatch, point, currentUser, customerId]);

  const scrapeData = readers?.filter((item) => {
    const configuration = item.deviceType;
    return configuration !== 3 && configuration !== 4;
  });

  const filteredData = useMemo(() => {
    let data = scrapeData ? scrapeData : [];
    if (filters.serialNumber !== '') {
      data = Search(data, 'serialNumber', filters.serialNumber);
    }
    if (filters.accessPoint !== '') {
      data = Search(data, 'accessPoint', filters.accessPoint);
    }

    if (filters.formattedFirmwareVersion !== '') {
      data = Search(data, 'formattedFirmwareVersion', filters?.formattedFirmwareVersion);
    }
    if (filters.macId !== '') {
      data = Search(data, 'macId', filters.macId);
    }

    if (filters.status !== '') {
      data = Search(data, 'formattedStatus', filters.status);
    }

    if (filters.deviceType?.id) {
      data = data?.filter(d => Number(filters?.deviceType?.id) === d?.deviceType) || []
    }

    if (filters.siteName !== '') {
      data = Search(data, 'siteName', filters.siteName);
    }

    if (!isMobile) {
      data = data.slice(
        (pagination.currentPage - 1) * 7,
        pagination.currentPage * pagination.perPage,
      );
    }

    return data;
  }, [
    filters.accessPoint,
    filters.deviceType,
    filters.formattedFirmwareVersion,
    filters.macId,
    filters.serialNumber,
    filters.status,
    filters.siteName,
    pagination.currentPage,
    pagination.perPage,
    scrapeData,
    isMobile
  ]);

  const hSearchChange = (e) => {
    const name = e.target.name;
    const value = e.target.value;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const search = {
    onChange: hSearchChange,
  };

  const changePage = (p) => {
    const { page, per_page } = p;
    setPagination(prev => ({
      ...prev,
      currentPage: page,
      perPage: per_page,
    }));
  };


  return (
    <Fragment>
      <div className='updateDetail-container'>
        <p>Firmware Update</p>
        <OutlineButton shadow={false} text='View' onClick={() => { setFirmwareDetails(true) }} />
      </div>
      <div className="spacer__1" />
      <div>
        <FlexRow justifyContent='space-between' styles={{ marginBottom: '1rem' }}>
          <Typography variant={"h7M"}>Readers</Typography>
          <SButton
            minWidth
            disabled={selectedReaders?.length <= 0}
            onClick={() => {
              setFirmwareUpdate({ show: true, bulkUpdate: true })
            }}
          >
            Update Firmware
          </SButton>
        </FlexRow>
        <div item xs={12} sm={12} md={12} lg={12}>
          {isMobile ?
            <MobileTable
              columns={columns?.commonColumns}
              data={filteredData || []}
              actions={columns?.actions}
              selection={{
                enabled: true,
                onSelectionChange: (list) => { setSelectedReaders(list) },
                notSelect: filteredData?.filter(ap => !ap?.updateAllowed || ap?.status !== 1)?.map(ap => ap.id) || []
              }}
              filters={filters}
              setFilters={setFilters}
              filtersConfig={mobileFiltersConfig}
              emptyMessage={'No Readers Found'}
            />
            : <Table
              columns={columns?.allColumns}
              data={filteredData || []}
              defaultMessage={'No Readers Found'}
              pagination={pagination}
              options={{ search, selection: { enable: true, notselect: filteredData?.filter(ap => !ap?.updateAllowed || ap?.status !== 1)?.map(ap => ap.id) || [], onChange: (list) => { setSelectedReaders(list) } } }}
              changePage={changePage}
            />}
        </div>
        <Menu
          anchorEl={menu.anchorEl}
          keepMounted
          open={menu.show}
          getContentAnchorEl={null}
          transformOrigin={{ vertical: 'center', horizontal: 'right' }}
          anchorOrigin={{ vertical: 'center', horizontal: 'left' }}
          classes={{ paper: classes.paper, list: classes.list }}
          onClose={() => { setMenu({ ...menu, show: false }); }}>
          <MenuItem
            disabled={!(filteredData[Number(menu?.sItem?.row)]?.updateAllowed && filteredData[Number(menu?.sItem?.row)]?.status === 1)}
            className={classes.menuItemRoot}
            dense
            onClick={() => {
              setFirmwareUpdate({ show: true, bulkUpdate: false });
              setMenu({ ...menu, show: false });
            }}>
            Update Firmware
          </MenuItem>
          <MenuItem
            disabled={!filteredData[Number(menu?.sItem?.row)]?.rollbackAllowed}
            className={classes.menuItemRoot}
            dense
            onClick={() => {

            }}>
            Roll-Back Update
          </MenuItem>
          {/* <MenuItem
            className={classes.menuItemRoot}
            style={{ color: 'red' }}
            dense
            onClick={() => {

            }}>
            Delete Access Point
          </MenuItem> */}
        </Menu>
      </div>
      {firmwareUpdate?.show && <UpdateFirmware
        open={firmwareUpdate?.show}
        selectedReaders={selectedReaders}
        readerDetails={filteredData[Number(menu?.sItem?.row)]}
        bulkUpdate={firmwareUpdate?.bulkUpdate}
        onClose={() => {
          setFirmwareUpdate(prev => ({ ...prev, show: false }))
        }} />}

      <FirmwareUpdateDetails open={firmwareDetails} onClose={() => { setFirmwareDetails(false) }} sites={sites} readers={readers} />
    </Fragment>
  );
};

export default ACUTab;
